import {
  Accordion,
  AccordionVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Calendar,
  CalendarView,
  ColorClass,
  ColorCodes,
  FormMessage,
  FormMessageVariant,
  Icon,
  IconIdentifier,
  Input,
  InputGroup,
  SwitchCard,
  type SelectOption,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import { type FormatOptionLabelMeta } from 'react-select';
import {
  DashboardCategoryEnum,
  type DashboardDataCategoryEntry,
} from 'shared/api';
import { LeadDistanceCard } from './lead-distance-card';
import { ClusterDetailsAccordion } from './cluster-details-accordion';
import { ClusterVolumeMeasurements } from './cluster-volume-measurements';
import { VOLUME_TYPE_OPTIONS } from './constants';
import {
  type DashboardBaseFields,
  type DashboardConfigurationProps,
  type LeadDistanceEntry,
  type LeadDistanceOptionFormatter,
  type LeadDistanceSelectStyles,
} from './types';
import { useLeadDistances } from './use-lead-distances';
import { useInputFields } from 'shared/hooks';

// Constants.
const CURRENT_MONTH = `${new Date().getFullYear()}-${String(
  new Date().getMonth() + 1,
).padStart(2, '0')}`;

export const DashboardConfiguration: React.FC<DashboardConfigurationProps> = ({
  selectedDashboardCluster,
  dashboardFormValues,
  setDashboardFormValues,
  setIsNextDisabled,
  isLeadDistanceEnabled,
  setIsLeadDistanceEnabled,
  dashboardCategoryConfig,
}) => {
  // Refs.
  const leadDistanceCardRefs = useRef<Record<string, HTMLDivElement | null>>(
    {},
  );

  // In edit mode, lead distances that already have saved data start collapsed.
  // Add mode and any newly added cards stay expanded.
  const initiallyCollapsedLeadDistanceIds = useRef<Set<string>>(
    new Set<string>(
      selectedDashboardCluster.includeInDashboard &&
      selectedDashboardCluster.includeLeadDistanceInfo
        ? dashboardFormValues.leadDistances.map((entry) => entry.clientId)
        : [],
    ),
  );

  // Hooks.
  const { values, names, errors, setValues, onBlur, onFocus } =
    useInputFields<DashboardBaseFields>({
      category1: dashboardFormValues.category1,
      category2: dashboardFormValues.category2,
      accountingMonth: dashboardFormValues.accountingMonth,
      volumeType: dashboardFormValues.volumeType,
    });

  const leadDistance = useLeadDistances({
    initialEntries: dashboardFormValues.leadDistances,
    initialDeletedIds: dashboardFormValues.deletedLeadDistanceIds,
  });

  // Helpers.
  const getDashboardCategoryByKey = (categoryKey: DashboardCategoryEnum) =>
    dashboardCategoryConfig.find(
      (category) => category.categoryKey === categoryKey,
    );

  // Variables.
  const { category1, category2, accountingMonth, volumeType } = values;

  const isBaseFieldsValid =
    !isNil(category1) && !isNil(category2) && !isNil(accountingMonth);

  const isLeadDistanceFieldsValid =
    !isLeadDistanceEnabled || leadDistance.areEntriesValid;

  const isNextStepDisabled = !(isBaseFieldsValid && isLeadDistanceFieldsValid);

  const category1Config = getDashboardCategoryByKey(
    DashboardCategoryEnum.Category1,
  );

  const category2Config = getDashboardCategoryByKey(
    DashboardCategoryEnum.Category2,
  );

  const category3Config = getDashboardCategoryByKey(
    DashboardCategoryEnum.Category3,
  );

  const category4Config = getDashboardCategoryByKey(
    DashboardCategoryEnum.Category4,
  );

  const hasNoCategory1Options = isEmpty(category1Config?.options);
  const hasNoCategory2Options = isEmpty(category2Config?.options);

  const volumeFormMessage = (() => {
    if (hasNoCategory1Options && hasNoCategory2Options) {
      return `No ${category1Config?.name?.toLowerCase()} and ${category2Config?.name?.toLowerCase()} entries available. Please contact your admin to create them.`;
    }

    if (hasNoCategory1Options) {
      return `No ${category1Config?.name?.toLowerCase()} entries available. Please contact your admin to create them.`;
    }

    if (hasNoCategory2Options) {
      return `No ${category2Config?.name?.toLowerCase()} entries available. Please contact your admin to create them.`;
    }

    return null;
  })();

  const leadDistanceFormMessage = (() => {
    if (!isLeadDistanceEnabled) {
      return null;
    }

    if (isEmpty(category3Config?.options)) {
      return `No ${category3Config?.name?.toLowerCase()} entries available. Please contact your admin to create them.`;
    }

    return null;
  })();

  const selectStyles: LeadDistanceSelectStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.data.value.isActive
        ? provided.color
        : `${ColorCodes.Neutral200} !important`,
    }),
  };

  // Effects.
  useEffect(() => {
    setDashboardFormValues({
      ...values,
      leadDistances: leadDistance.entries,
      deletedLeadDistanceIds: leadDistance.deletedIds,
      includeLeadDistanceInfo: isLeadDistanceEnabled,
    });
  }, [
    values,
    leadDistance.entries,
    leadDistance.deletedIds,
    isLeadDistanceEnabled,
  ]);

  useEffect(() => {
    setIsNextDisabled(isNextStepDisabled);
  }, [isNextStepDisabled]);

  useEffect(() => {
    if (isNil(leadDistance.scrollToClientId)) {
      return;
    }

    const timeout = setTimeout(() => {
      leadDistanceCardRefs.current[
        leadDistance.scrollToClientId as string
      ]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      leadDistance.clearScrollTarget();
    }, 100);

    return () => clearTimeout(timeout);
  }, [leadDistance.scrollToClientId]);

  // Handlers.
  const onToggleLeadDistance = () => {
    leadDistance.resetTransientState();

    setIsLeadDistanceEnabled((prev) => {
      const isEnabling = !prev;

      if (isEnabling) {
        leadDistance.ensureEntryExists();
      }

      return isEnabling;
    });
  };

  const handleSelectChange =
    (field: keyof DashboardBaseFields) => (selectedOption: SelectOption) => {
      setValues((prev) => ({
        ...prev,
        [field]: selectedOption,
      }));
    };

  const handleAccountingMonthChange = (value?: Date | null) => {
    setValues((prev) => ({
      ...prev,
      accountingMonth: value ?? null,
    }));
  };

  const formatOptionLabel: LeadDistanceOptionFormatter = (
    option: SelectOption<DashboardDataCategoryEntry>,
    meta: FormatOptionLabelMeta<SelectOption<DashboardDataCategoryEntry>>,
  ) => {
    const selectedOption = meta.selectValue?.[0];

    return (
      <div className="dashboard-modal__select-option">
        <span>{option.label}</span>

        {!option.value.isActive &&
          meta.context === 'value' &&
          option.value.id === selectedOption?.value.id && (
            <span className="dashboard-modal__select-option__status-pill">
              Deactivated
            </span>
          )}
      </div>
    );
  };

  const sortOptionsByActive = (
    options?: SelectOption<DashboardDataCategoryEntry>[],
  ): SelectOption<DashboardDataCategoryEntry>[] => {
    if (isNil(options) || isEmpty(options)) {
      return [];
    }

    return [...options].sort(
      (option1, option2) =>
        Number(option2.value.isActive) - Number(option1.value.isActive),
    );
  };

  const renderLeadDistanceTitle = (entry: LeadDistanceEntry, index: number) => {
    const showRemove = leadDistance.entries.length > 1;
    const category3Label = entry.category3?.label;

    return (
      <div className="dashboard-modal__lead-distance__accordion-title">
        <span className="dashboard-modal__lead-distance__accordion-title-text">
          {`Lead distance ${index + 1}`}

          {!isNil(category3Label) && (
            <span className="dashboard-modal__lead-distance__accordion-title-sub">
              {category3Label}
            </span>
          )}
        </span>

        {showRemove && (
          <span
            className="dashboard-modal__lead-distance__card-remove"
            title={`Remove lead distance ${index + 1}`}
            onClick={(event) => {
              event.stopPropagation();
              leadDistance.onRemoveEntry(entry.clientId);
            }}
          >
            <Icon
              identifier={IconIdentifier.Bin}
              size={18}
              colorClass={ColorClass.Red500}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-modal__configuration">
      {/* Cluster Details Accordion */}
      <ClusterDetailsAccordion
        selectedDashboardCluster={selectedDashboardCluster}
      >
        <div className="dashboard-modal__volume-selection">
          <InputGroup className="dashboard-modal__volume-selection__type">
            <Input.Label isRequired>Volume Type</Input.Label>
            <Input.Select
              placeholder="Select"
              options={VOLUME_TYPE_OPTIONS}
              name={names.volumeType}
              value={volumeType}
              error={errors.volumeType}
              onChange={handleSelectChange(names.volumeType)}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <ClusterVolumeMeasurements
            selectedDashboardCluster={selectedDashboardCluster}
            volumeType={volumeType.value}
          />
        </div>
      </ClusterDetailsAccordion>

      <div className="dashboard-modal__volume-details__container">
        <div className="dashboard-modal__volume-details">
          <InputGroup>
            <Input.Label isRequired>{category1Config?.name}</Input.Label>
            <Input.Select
              placeholder="Select"
              options={sortOptionsByActive(category1Config?.options)}
              name={names.category1}
              value={category1}
              error={errors.category1}
              isDisabled={isEmpty(category1Config?.options)}
              isOptionDisabled={(
                option: SelectOption<DashboardDataCategoryEntry>,
              ) => !option.value.isActive}
              formatOptionLabel={formatOptionLabel}
              onChange={handleSelectChange(names.category1)}
              styles={selectStyles}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <InputGroup>
            <Input.Label isRequired>{category2Config?.name}</Input.Label>
            <Input.Select
              placeholder="Select"
              options={sortOptionsByActive(category2Config?.options)}
              name={names.category2}
              value={category2}
              error={errors.category2}
              isDisabled={isEmpty(category2Config?.options)}
              isOptionDisabled={(
                option: SelectOption<DashboardDataCategoryEntry>,
              ) => !option.value.isActive}
              formatOptionLabel={formatOptionLabel}
              styles={selectStyles}
              onChange={handleSelectChange(names.category2)}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>

          <InputGroup className="dashboard-modal__volume-details__accounting-month">
            <Input.Label isRequired>Accounting Month</Input.Label>

            <Calendar
              view={CalendarView.Month}
              value={accountingMonth}
              onChange={handleAccountingMonthChange}
              dateFormat="M YY"
              placeholder="Start Month"
              isClearable={false}
              showIcon={false}
              maxDate={CURRENT_MONTH}
            />
          </InputGroup>
        </div>

        {!isNil(volumeFormMessage) && (
          <FormMessage
            variant={FormMessageVariant.Error}
            message={volumeFormMessage}
          />
        )}
      </div>

      {/* Lead Distance Section */}
      <div className="dashboard-modal__lead-distance">
        <InputGroup>
          <SwitchCard
            title="Add lead distance"
            checked={isLeadDistanceEnabled}
            onClick={onToggleLeadDistance}
          />
        </InputGroup>

        <div
          className={classNames('dashboard-modal__lead-distance__container', {
            'dashboard-modal__lead-distance__container--collapsed':
              !isLeadDistanceEnabled,
          })}
        >
          <div className="dashboard-modal__lead-distance__cards">
            {leadDistance.entries.map((entry, index) => (
              <div
                key={entry.clientId}
                ref={(element) => {
                  leadDistanceCardRefs.current[entry.clientId] = element;
                }}
              >
                <Accordion
                  variant={AccordionVariant.ChevronLeft}
                  defaultActiveKey={
                    initiallyCollapsedLeadDistanceIds.current.has(
                      entry.clientId,
                    )
                      ? undefined
                      : entry.clientId
                  }
                >
                  <Accordion.Item
                    eventKey={entry.clientId}
                    title={renderLeadDistanceTitle(entry, index)}
                  >
                    <LeadDistanceCard
                      entry={entry}
                      errors={leadDistance.errors[entry.clientId] ?? {}}
                      category3Config={category3Config}
                      category4Config={category4Config}
                      selectStyles={selectStyles}
                      sortOptionsByActive={sortOptionsByActive}
                      formatOptionLabel={formatOptionLabel}
                      onSelectChange={leadDistance.onSelectChange}
                      onNumberChange={leadDistance.onNumberChange}
                      onFieldBlur={leadDistance.onFieldBlur}
                      onFieldFocus={leadDistance.onFieldFocus}
                    />
                  </Accordion.Item>
                </Accordion>
              </div>
            ))}
          </div>

          <div className="dashboard-modal__lead-distance__add">
            <Button
              className="dashboard-modal__lead-distance__add-button"
              variant={ButtonVariant.Outline}
              size={ButtonSize.Small}
              leftIconIdentifier={IconIdentifier.Plus}
              onClick={leadDistance.onAddEntry}
            >
              Add lead distance
            </Button>
          </div>

          {!isNil(leadDistanceFormMessage) && (
            <FormMessage
              variant={FormMessageVariant.Error}
              message={leadDistanceFormMessage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
